import { FramingChartProps } from './types';
import {
  drawSensorArea,
  drawEffectiveCanvas,
  drawFramingDecisionProtectionArea,
  drawFramingDecisionArea,
} from './drawing/FramingChartAreas';
import {
  drawChartText,
  drawTestPatterns,
  drawNetflixLogo,
  drawCenterCross,
} from './drawing/FramingChartElements';
import { drawDimensionAndPixelLabels } from './drawing/FramingChartLabels';
import { NETFLIX_LOGO_PATH } from '../../constants';
import { isValidResolution } from './validators/validateResolution';

/**
 * Helper function to load an image asset and return it as an HTMLImageElement
 */
async function loadImageAsset(
  src: string
): Promise<HTMLImageElement | undefined> {
  return new Promise((resolve) => {
    const imageAsset = new Image();
    imageAsset.src = src;
    imageAsset.onload = () => {
      resolve(imageAsset);
    };

    // Still resolve even if the image fails to load
    imageAsset.onerror = () => {
      console.warn(`Failed to load image asset: ${src}`);
      resolve(undefined);
    };
  });
}

/**
 * Renders a framing chart on the provided canvas
 * @returns A promise that resolves when all drawing operations are complete
 */
export default async function renderFramingChart(
  canvas: HTMLCanvasElement,
  props: FramingChartProps
): Promise<void> {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const {
    framingDecisionProtectionCoordinates,
    framingDecisionProtection,
    canvasResolution,
    centerCrossCoordinates,
    centerCrossResolution,
    chartText,
    framingDecisionCoordinates,
    framingDecision,
    effectiveCanvasCoordinates,
    effectiveCanvas,
    logoCoordinates,
    logoResolution,
    testPatternCoordinates,
    testPatternResolution,
    textCoordinates,
    textScale,
    triangleResolution,
    pixelLabels,
    dimensionLabels,
  } = props;

  // Validate canvas dimensions before assignment to prevent "cannot be parsed, or is out of range" error
  if (!isValidResolution(canvasResolution)) {
    console.warn(
      'Invalid canvas dimensions:',
      canvasResolution,
      'Skipping chart render.'
    );
    return;
  }
  const { width, height } = canvasResolution;
  canvas.width = width;
  canvas.height = height;

  drawSensorArea(ctx, canvasResolution, triangleResolution);

  if (effectiveCanvas && effectiveCanvasCoordinates) {
    drawEffectiveCanvas(
      ctx,
      effectiveCanvas,
      effectiveCanvasCoordinates,
      triangleResolution
    );
  }

  drawFramingDecisionProtectionArea(
    ctx,
    framingDecisionProtection,
    framingDecisionProtectionCoordinates,
    triangleResolution
  );

  drawFramingDecisionArea(
    ctx,
    framingDecision,
    framingDecisionCoordinates,
    triangleResolution
  );

  drawChartText(ctx, chartText, textCoordinates, textScale);

  drawTestPatterns(ctx, testPatternResolution, testPatternCoordinates);

  drawCenterCross(ctx, centerCrossResolution, centerCrossCoordinates);

  const logoImage = await loadImageAsset(NETFLIX_LOGO_PATH);
  if (logoImage) {
    drawNetflixLogo(ctx, logoImage, logoResolution, logoCoordinates);
  }

  drawDimensionAndPixelLabels(ctx, pixelLabels, dimensionLabels);
}
